import axios from 'axios';
import { API_CONFIG, getDefaultHeaders } from '../config/api';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.apiClient = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      headers: getDefaultHeaders()
    });

    // Interceptor para adicionar token automaticamente
    this.apiClient.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para lidar com respostas de erro
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Autentica o usuário e retorna um JWT token
   * @param {Object} credentials - Credenciais do usuário
   * @returns {Promise<Object>} - Token JWT e informações do usuário
   */
  async authenticate(credentials) {
    try {
      const response = await this.apiClient.post(API_CONFIG.ENDPOINTS.AUTH, credentials);
      
      if (response.data && response.data.token) {
        this.token = response.data.token;
        localStorage.setItem('authToken', this.token);
      }

      return {
        success: true,
        data: response.data,
        message: 'Autenticação realizada com sucesso'
      };
    } catch (error) {
      console.error('Erro na autenticação:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Erro na autenticação'
      };
    }
  }

  /**
   * Verifica se o usuário está autenticado
   * @returns {boolean} - Status de autenticação
   */
  isAuthenticated() {
    return !!this.token;
  }

  /**
   * Obtém o token atual
   * @returns {string|null} - Token JWT
   */
  getToken() {
    return this.token;
  }

  /**
   * Define o token manualmente
   * @param {string} token - Token JWT
   */
  setToken(token) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  /**
   * Realiza logout do usuário
   */
  logout() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  /**
   * Obtém informações do usuário a partir do token
   * @returns {Object|null} - Informações do usuário decodificadas
   */
  getUserInfo() {
    if (!this.token) return null;

    try {
      // Decodifica o payload do JWT (base64)
      const payload = this.token.split('.')[1];
      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  /**
   * Verifica se o token está expirado
   * @returns {boolean} - Status de expiração
   */
  isTokenExpired() {
    const userInfo = this.getUserInfo();
    if (!userInfo || !userInfo.exp) return true;

    const currentTime = Date.now() / 1000;
    return userInfo.exp < currentTime;
  }
}

// Exporta uma instância singleton
export default new AuthService();
